import React, { useEffect, useState } from "react";
import { PlusCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Dropdown,
  Empty,
  Form,
  Input,
  Modal,
  Pagination,
  Row,
  Space,
  Typography,
} from "antd";
import { Link } from "react-router-dom";
import MoreIcon from "../../assets/images/menu-icon.svg";
import { useAxios } from "../../hooks/useAxios";
import SearchInput from "../FormFields/SearchInput";
import { useNotification } from "../../contexts/NotificationProvider";
import departmentSvg from "../../assets/svgs/department.svg";
import { useScreenSize } from "../../hooks/useScreenSize";

const { Text } = Typography;

const DepartmentsView = () => {
  const { axiosInstance } = useAxios();
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedDepartrmentId, setSelectedDepartmentId] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const notify = useNotification();
  const [form] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1); // Add currentPage state

  const isLargeScreen = useScreenSize(); // Custom hook to check screen size
  const pageSize = isLargeScreen ? 9 : 6;

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    setSelectedDepartment(null);
    setSelectedDepartmentId(null);
  };
  const createDepartment = async (formData) => {
    setLoading(true);
    const { title } = formData;

    const requestBody = {
      name: title,
    };
    try {
      const response =
        selectedDepartrmentId === null
          ? await axiosInstance.post("/api/departments/", requestBody)
          : await axiosInstance.put(
              `/api/departments/${selectedDepartrmentId}/`,
              requestBody
            );
      setDepartments((previous) =>
        selectedDepartrmentId === null
          ? [response.data, ...previous]
          : previous.map((department) =>
              department.id === selectedDepartrmentId ? response.data : department
            )
      );
      notify({
        type: "success",
        message: `Department ${
          selectedDepartrmentId ? "updated" : "created"
        } successfully`,
      });
      handleCancel();
    } catch (error) {
      const details = error.response?.data;
      notify({
        type: "error",
        message: "Department not saved",
        description:
          details?.error ||
          details?.name?.join?.(" ") ||
          "Please check the department name and try again.",
      });
    } finally {
      setLoading(false);
    }
  };
  const confirmDelete = async (id) => {
    setLoading(true);
    try {
      await axiosInstance.delete(`/api/departments/${id}/`);
      await fetchDepartments();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setIsModalOpen(false);
      form.resetFields();
      setSelectedDepartmentId(null);
      setSelectedDepartment(null);
    }
  };

  const showModal = () => {
    setIsModalOpen(true);
    setSelectedDepartmentId(null);
    setSelectedDepartment(null);
  };

  const fetchDepartments = async (searchTerm = "") => {
    setLoading(true);
    try {
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await axiosInstance.get("/api/departments/", { params });
      setDepartments(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (searchTerm) => {
    setHasSearched(true);
    await fetchDepartments(searchTerm);
  };

  const getMenu = (id) => {
    const items = [
      {
        label: <Text>Edit Department</Text>,
        key: "0",
        meta: {
          type: "edit",
        },
      },
      {
        label: <Text type="danger">Delete</Text>,
        key: "1",
        meta: {
          type: "delete",
        },
      },
    ];
    const onClick = (e) => {
      const itemMeta = e.item.props.meta;

      if (itemMeta.type === "edit") {
        const currentDepartment = departments.find((d) => d.id === id);
        setSelectedDepartment(currentDepartment);
        setSelectedDepartmentId(id);
        form.setFieldsValue({ title: currentDepartment?.name }); // Pre-fill form
        setIsModalOpen(true);
      }

      if (itemMeta.type === "delete") {
        Modal.confirm({
          title: "Are you sure you want to delete?",
          content:
            "Deleting this department will also permanently remove all associated rotas. Are you sure you want to proceed?",
          onOk: () => confirmDelete(id),
          onCancel: () => handleCancel(),
        });
      }
      setSelectedDepartmentId(id);
    };
    return {
      items,
      onClick,
    };
  };
  useEffect(() => {
    fetchDepartments();
    setHasSearched(false);
    //eslint-disable-next-line
  }, [pageSize]);
  return (
    <>
      <Modal
        title={selectedDepartrmentId ? "Edit Department" : "Create Department "}
        open={isModalOpen}
        onCancel={handleCancel}
        onClose={handleCancel}
        wrapClassName="main-modal-class notification-modal"
        footer={[
          <Button
            style={{
              backgroundColor: "transparent",
              color: "#324054",
              border: "none",
              borderRadius: "4px",
              padding: "0 16px",
              height: "40px",
              fontWeight: 600,
              width: "118px",
            }}
            variant="text"
            onClick={handleCancel}
          >
            Cancel
          </Button>,
          <Button
            style={{
              backgroundColor: "#212180",
              color: "#FFF",
              border: "none",
              borderRadius: "4px",
              padding: "0 16px",
              height: "40px",
              fontWeight: 600,
              width: "118px",
            }}
            variant="text"
            htmlType="submit"
            loading={loading}
            disabled={loading}
            onClick={() => form.submit()}
          >
            {selectedDepartrmentId ? "Update" : "Create"}
          </Button>,
        ]}
        className="upload-csv-modal"
      >
        <Form
          form={form}
          layout="vertical"
          name="employeeForm"
          requiredMark={false}
          onFinish={createDepartment}
          initialValues={selectedDepartment}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[
              { required: true, message: "Title is required" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const trimmedValue = value?.trim() || "";
                  const department = departments.find(
                    (i) => i.id === selectedDepartrmentId
                  );

                  // Check if input is empty or just whitespace
                  if (!trimmedValue) {
                    return Promise.reject(
                      new Error("Title cannot be empty or just spaces")
                    );
                  }

                  // Check if title is same as current department title
                  if (
                    trimmedValue.toLowerCase() ===
                    department?.title?.trim().toLowerCase()
                  ) {
                    return Promise.reject(
                      new Error("Title is same as current title.")
                    );
                  }

                  // Disallow titles made of only numbers
                  if (/^\d+$/.test(trimmedValue)) {
                    return Promise.reject(
                      new Error("Title cannot be only numbers")
                    );
                  }

                  // Length constraint (assumed 50 characters max, update if needed)
                  if (trimmedValue.length > 50) {
                    return Promise.reject(
                      new Error("Title must be 50 characters or fewer")
                    );
                  }

                  // Allowed characters: letters, numbers, spaces, &, -, _, /, ., ( )
                  const allowedRegex = /^[a-zA-Z0-9 &\-_/().]+$/;
                  if (!allowedRegex.test(trimmedValue)) {
                    return Promise.reject(
                      new Error(
                        "Title can only include letters, numbers, spaces, &, -, _, /, ., and parentheses"
                      )
                    );
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input placeholder="Department title" />
          </Form.Item>
        </Form>
      </Modal>
      {departments.length > 0 || hasSearched ? (
        <div
          style={{
            marginBottom: 14,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Col xs={24} sm={24} md={12} lg={6}>
            <SearchInput
              onSearch={handleSearch}
              placeholder="Search by name..."
              style={{ width: "100%" }}
            />
          </Col>
          <Button
            style={{
              background: "#212180",
              color: "#fff",
              width: "190px",
              height: "40px",
              padding: "12px 15px",
            }}
            onClick={showModal}
            icon={<PlusCircleOutlined />}
          >
            Add Department
          </Button>
        </div>
      ) : null}
      {departments.length ? (
        <>
          <Row className="weeks-row" gutter={[24, 24]}>
            {departments
              .slice((currentPage - 1) * pageSize, currentPage * pageSize)
              .map((department, index) => {
                return (
                  <Col span={24} md={12} lg={8} key={index}>
                    <Card
                      bordered={false}
                      extra={
                        <Dropdown
                          menu={getMenu(department?.id)}
                          trigger={["click"]}
                        >
                          <img
                            src={MoreIcon}
                            alt="menu_icon"
                            style={{ cursor: "pointer" }}
                          />
                        </Dropdown>
                      }
                    >
                      <p style={{ fontWeight: "600", fontSize: "16px" }}>
                        {department?.name}
                      </p>
                      <Link
                        to={`/department/${department?.id}`}
                        style={{
                          width: "100%",
                          padding: "8px 10px 8px 10px",
                          borderRadius: "6px",
                          backgroundColor: "#F0F1F7",
                          color: "#212180",
                          fontSize: "12px",
                          fontWeight: "500",
                          lineHeight: "11.43px",
                          textAlign: "center",
                          marginTop: "20px",
                          marginBottom: "-8px",
                          zIndex: 2,
                        }}
                      >
                        View Department
                      </Link>

                      <div
                        style={{
                          position: "absolute",
                          bottom: "30px",
                          right: "5px",
                          width: "100px",
                          height: "100px",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <img src={departmentSvg} alt="" />
                      </div>
                    </Card>
                  </Col>
                );
              })}
          </Row>
          <div
            style={{
              position: "absolute",
              bottom: "0%",
              right: "0%",
              background: "#f5f5f5",
            }}
          >
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={departments.length}
              onChange={(page) => setCurrentPage(page)}
              className="custom-pagination"
            />
          </div>
        </>
      ) : (
        <Empty
          description={
            <Space direction="vertical">
              <Typography.Text>
                No department found. Please click the button below to add data.
              </Typography.Text>
              <Button
                type="default"
                onClick={showModal}
                icon={<PlusCircleOutlined />}
              >
                Create Department
              </Button>
            </Space>
          }
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: "20px",
          }}
        />
      )}
    </>
  );
};

export default React.memo(DepartmentsView);
